// Overlay curated protection/swap TERMS onto imported draft picks at league creation.
//
// Pick OWNERSHIP (tid = controller, originalTid = origin) is already correct from the real-player
// data. This only adds the terms — protectedTop / swap — that the source data doesn't carry,
// matching each obligation to the pick it describes by (originalTid, season, round). Pure and
// testable; the caller supplies an abbrev->tid resolver and the picks to annotate.

export type PickObligation = {
	originalAbbrev: string;
	season: number;
	round: number;
	protectedTop?: number;
	rolloverSeasons?: number[];
	swapWithAbbrev?: string;
	favorable?: boolean;
};

type PatchablePick = {
	originalTid: number;
	season: number | string;
	round: number;
	protection?: { protectedTop: number; rolloverSeasons?: number[] };
	swap?: { swapTid: number; favorable: boolean };
};

// Returns the number of obligations that matched a pick (for logging / sanity).
export const patchPickObligations = (
	draftPicks: PatchablePick[],
	obligations: PickObligation[],
	abbrevToTid: (abbrev: string) => number | undefined,
): number => {
	let matched = 0;

	for (const o of obligations) {
		const originalTid = abbrevToTid(o.originalAbbrev);
		if (originalTid === undefined) {
			continue;
		}

		const dp = draftPicks.find(
			(p) =>
				p.originalTid === originalTid &&
				p.season === o.season &&
				p.round === o.round,
		);
		if (!dp) {
			continue;
		}

		if (o.protectedTop !== undefined) {
			dp.protection = {
				protectedTop: o.protectedTop,
				rolloverSeasons: o.rolloverSeasons,
			};
		}

		if (o.swapWithAbbrev !== undefined) {
			const swapTid = abbrevToTid(o.swapWithAbbrev);
			if (swapTid !== undefined) {
				dp.swap = { swapTid, favorable: o.favorable ?? true };
			}
		}

		matched += 1;
	}

	return matched;
};
